import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../api/auth';
import MiraLinkLogo from '../components/MiraLinkLogo';

const EyeIcon = ({ hidden }) => (
  <svg
    aria-hidden="true"
    className="h-5 w-5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {hidden ? (
      <>
        <path d="M3 3l18 18" />
        <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
        <path d="M9.9 4.2A10.8 10.8 0 0 1 12 4c5 0 9 5 10 8a13.2 13.2 0 0 1-3.1 4.6" />
        <path d="M6.1 6.1A13.1 13.1 0 0 0 2 12c1 3 5 8 10 8 1.4 0 2.7-.4 3.9-1.1" />
      </>
    ) : (
      <>
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

const fieldClass =
  'h-14 w-full rounded-xl border border-white/8 bg-[#202124] px-4 text-[15px] font-medium text-white outline-none transition placeholder:text-zinc-500 hover:border-white/14 focus:border-cyan-400/80 focus:bg-[#24262a] focus:ring-4 focus:ring-cyan-400/10';

const AUTH_TIMEOUT_MS = 8000;

const withTimeout = (promise, message) => {
  let timeoutId;

  const timeout = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error(message)), AUTH_TIMEOUT_MS);
  });

  return Promise.race([promise, timeout]).finally(() => window.clearTimeout(timeoutId));
};

const persistSession = (result) => {
  try {
    localStorage.setItem('token', result.token);
    localStorage.setItem('user', JSON.stringify(result.user));
  } catch {
    throw new Error('Аккаунт создан, но браузер не дал сохранить вход. Включите cookies/localStorage и попробуйте войти.');
  }
};

const Register = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!loading) return undefined;

    const timeoutId = window.setTimeout(() => {
      setLoading(false);
      setError('Регистрация зависла. Причина: браузер или старая сборка не вернули ответ вовремя. Обновите страницу и попробуйте снова.');
    }, AUTH_TIMEOUT_MS + 1000);

    return () => window.clearTimeout(timeoutId);
  }, [loading]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await withTimeout(
        authAPI.register(email.trim(), username.trim(), password),
        'Регистрация заняла слишком много времени. Обновите страницу и попробуйте еще раз.'
      );
      if (!result?.token || !result?.user) {
        throw new Error('Регистрация не вернула данные аккаунта. Попробуйте еще раз.');
      }
      persistSession(result);
      setLoading(false);
      navigate('/messenger');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Не удалось создать аккаунт. Проверьте данные и попробуйте еще раз.');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = email.trim() && username.trim() && password.length >= 6;

  return (
    <main className="min-h-screen bg-[#101113] px-4 py-6 text-white sm:px-6 lg:px-8">
      <section className="mx-auto flex min-h-[calc(100vh-48px)] w-full max-w-6xl items-center justify-center">
        <div className="grid w-full items-center gap-10 lg:grid-cols-[1fr_440px]">
          <div className="hidden max-w-xl lg:block">
            <MiraLinkLogo size={88} />
            <h1 className="mt-8 max-w-[560px] text-6xl font-black leading-[0.96] tracking-normal">
              Создание аккаунта
            </h1>
            <p className="mt-5 max-w-md text-lg leading-7 text-zinc-400">
              Регистрация по email. Username сразу станет вашей публичной ссылкой в MiraLink.
            </p>
          </div>

          <div className="mx-auto w-full max-w-[440px]">
            <div className="mb-8 text-center lg:hidden">
              <div className="mb-5 flex justify-center">
                <MiraLinkLogo size={74} />
              </div>
              <h1 className="text-[34px] font-black leading-none tracking-normal">Создание аккаунта</h1>
              <p className="mt-3 text-sm leading-6 text-zinc-400">Регистрация только через электронную почту</p>
            </div>

            <div className="rounded-2xl border border-white/8 bg-[#17181b] p-5 shadow-2xl shadow-black/30 sm:p-6">
              {error && (
                <div
                  className="mb-5 rounded-xl border border-red-400/25 bg-red-500/10 px-4 py-3 text-sm leading-5 text-red-100"
                  data-testid="error-message"
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleRegister} className="space-y-5">
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-semibold text-zinc-200">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@mail.com"
                    autoComplete="email"
                    className={fieldClass}
                    required
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <label htmlFor="username" className="mb-2 block text-sm font-semibold text-zinc-200">
                    Имя пользователя
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[15px] font-semibold text-zinc-500">
                      @
                    </span>
                    <input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="username"
                      autoComplete="username"
                      className={`${fieldClass} pl-9`}
                      required
                      pattern="[a-zA-Z0-9_]{3,30}"
                      data-testid="username-input"
                    />
                  </div>
                  <p className="mt-2 text-xs leading-5 text-zinc-500">
                    От 3 до 30 символов: латиница, цифры и нижнее подчеркивание.
                  </p>
                </div>

                <div>
                  <label htmlFor="password" className="mb-2 block text-sm font-semibold text-zinc-200">
                    Пароль
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Введите пароль"
                      autoComplete="new-password"
                      className={`${fieldClass} pr-12`}
                      required
                      minLength="6"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      className="absolute inset-y-0 right-3 flex w-9 items-center justify-center rounded-lg text-cyan-400 transition hover:bg-white/5 hover:text-cyan-200"
                      aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                      data-testid="toggle-password"
                    >
                      <EyeIcon hidden={showPassword} />
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !canSubmit}
                  className="mt-2 h-14 w-full rounded-xl bg-cyan-400 text-[15px] font-extrabold text-[#071316] transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:bg-[#34343a] disabled:text-zinc-500"
                  data-testid="register-button"
                >
                  {loading ? 'Создаем аккаунт...' : 'Зарегистрироваться'}
                </button>
              </form>
            </div>

            <div className="mt-6 text-center text-sm text-zinc-400">
              Уже есть аккаунт?{' '}
              <button
                onClick={() => navigate('/login')}
                className="font-bold text-cyan-400 transition hover:text-cyan-200"
                data-testid="go-to-login"
              >
                Войти
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Register;
